import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import { CheckCircleOutlined, FileDownloadOutlined, GppMaybeOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { getTimezone } from "../../../../functions/functions";
import { VehicleDriverChangeType, VehicleGridType } from "../../../../types/types";

type Props = {
    vehicle: VehicleGridType;
    history: VehicleDriverChangeType[];
    compact?: boolean;
    error?: boolean;
    loading?: boolean;
};

const getIdentifier = (vehicle: VehicleGridType) =>
    vehicle.driver?.driverIdentifier?.trim() || vehicle.driverUnrecorded?.trim() || null;

const formatDate = (value: string | null | undefined, timezone: string | null) => {
    if (!value) return "—";
    const date = dayjs.utc(value);
    return date.isValid() ? date.tz(getTimezone(timezone)).format("DD/MM/YYYY HH:mm:ss") : "—";
};

// Controle de condutores: null/undefined quando o cliente não usa o controle.
const authorizationLabel = (authorized?: boolean | null) =>
    authorized == null ? "" : authorized ? "Autorizado" : "Não autorizado";

const AuthorizationFlag = ({ authorized, compact }: { authorized?: boolean | null; compact: boolean }) => {
    if (authorized == null) return null;
    const color = authorized ? "#15803d" : "#dc2626";
    const Icon = authorized ? CheckCircleOutlined : GppMaybeOutlined;
    return (
        <Tooltip
            arrow
            title={authorized ? "Condutor liberado para este veículo." : "Condutor não liberado para este veículo."}
        >
            <Box
                component="span"
                sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.25, ml: 0.75, px: 0.5,
                    border: `1px solid ${color}`, borderRadius: 1, color, fontWeight: 700,
                    fontSize: compact ? 9 : 10.5, lineHeight: 1.6, verticalAlign: "middle",
                }}
            >
                <Icon sx={{ fontSize: compact ? 11 : 13 }} />
                {authorizationLabel(authorized)}
            </Box>
        </Tooltip>
    );
};

const DriverChangeSummary = ({ vehicle, history, compact = false, error = false, loading = false }: Props) => {
    const [observedChanges, setObservedChanges] = useState<VehicleDriverChangeType[]>([]);
    const lastObservation = useRef<{ identifier: string | null; time: number } | null>(null);
    const identifier = getIdentifier(vehicle);
    const fixTime = vehicle.position?.fixTime;
    const fixTimeMs = fixTime ? dayjs.utc(fixTime).valueOf() : NaN;

    // Usa somente as posições já recebidas pelo Ao vivo; não consulta histórico a cada 10 segundos.
    useEffect(() => {
        if (!Number.isFinite(fixTimeMs)) return;
        const previous = lastObservation.current;
        if (previous && fixTimeMs <= previous.time) return;
        lastObservation.current = { identifier, time: fixTimeMs };
        if (!previous || !identifier || identifier === previous.identifier) return;

        setObservedChanges(changes => [{
            driverId: vehicle.driver?.id ?? null,
            driverName: vehicle.driver?.name ?? null,
            driverIdentifier: identifier,
            identifiedAtUtc: new Date(fixTimeMs).toISOString(),
            driverAuthorized: vehicle.driverAuthorized,
        }, ...changes].slice(0, 5));
    }, [fixTimeMs, identifier, vehicle.driver?.id, vehicle.driver?.name, vehicle.driverAuthorized]);

    const passages = useMemo(() => {
        const byTimeAndIdentifier = new Map<string, VehicleDriverChangeType>();
        [...history, ...observedChanges].forEach(change => {
            byTimeAndIdentifier.set(`${Date.parse(change.identifiedAtUtc)}:${change.driverIdentifier}`, change);
        });
        return [...byTimeAndIdentifier.values()]
            .sort((a, b) => Date.parse(b.identifiedAtUtc) - Date.parse(a.identifiedAtUtc))
            .slice(0, 5);
    }, [history, observedChanges]);

    const isCurrentDriver = (change: VehicleDriverChangeType) =>
        vehicle.driver?.id != null && change.driverId != null
            ? change.driverId === vehicle.driver.id
            : change.driverIdentifier === identifier;

    const latestPassage = passages.find(change =>
        !Number.isFinite(fixTimeMs) || Date.parse(change.identifiedAtUtc) <= fixTimeMs
    );
    const currentStart = latestPassage && isCurrentDriver(latestPassage) ? latestPassage : null;
    const previous = passages.find(change =>
        !isCurrentDriver(change) &&
        (!currentStart || Date.parse(change.identifiedAtUtc) < Date.parse(currentStart.identifiedAtUtc))
    );
    const driverLabel = (change: VehicleDriverChangeType) =>
        change.driverName || `Identificador ${change.driverIdentifier}`;

    const exportCsv = () => {
        const timezone = getTimezone(vehicle.utcZone);
        const rows = passages.map(change => [
            vehicle.vehiclePlate,
            change === currentStart ? "Atual" : change === previous ? "Anterior" : "Histórico",
            driverLabel(change),
            change.driverIdentifier,
            formatDate(change.identifiedAtUtc, vehicle.utcZone),
            "Identificação registrada",
            authorizationLabel(change.driverAuthorized),
        ]);
        if (!currentStart && identifier) {
            rows.unshift([
                vehicle.vehiclePlate,
                "Atual",
                vehicle.driver?.name || `Identificador ${identifier}`,
                identifier,
                formatDate(fixTime, vehicle.utcZone),
                "Última posição recebida",
                authorizationLabel(vehicle.driverAuthorized),
            ]);
        }

        const escapeCsv = (value: string) => {
            const safeValue = /^\s*[=+\-@]/.test(value) ? `'${value}` : value;
            return `"${safeValue.replace(/"/g, '""')}"`;
        };
        const headers = ["Placa", "Situação", "Condutor", "Identificador", `Data/hora (${timezone})`, "Referência", "Liberação"];
        const csv = "\ufeff" + [headers, ...rows].map(row => row.map(escapeCsv).join(";")).join("\r\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `Condutores_${vehicle.vehiclePlate.replace(/[^a-zA-Z0-9_-]/g, "_")}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    return (
        <Box sx={{ p: compact ? 1.5 : 2, bgcolor: "#f8fafc", border: "1px solid rgba(0, 108, 119, 0.12)", borderRadius: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography sx={{ color: "#006c77", fontWeight: 800, fontSize: compact ? 11 : 14 }}>
                    Condutores do veículo
                </Typography>
                {!compact && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadOutlined />}
                        onClick={exportCsv}
                        disabled={loading || (passages.length === 0 && !identifier)}
                        sx={{ color: "#006c77", borderColor: "#006c77", whiteSpace: "nowrap" }}
                    >
                        Exportar CSV
                    </Button>
                )}
            </Box>
            <Typography sx={{ fontSize: compact ? 10.5 : 13 }}>
                <strong>Atual:</strong> {identifier ? (vehicle.driver?.name || `Identificador ${identifier}`) : "Não identificado"}
                {identifier && <AuthorizationFlag authorized={vehicle.driverAuthorized} compact={compact} />}
                {identifier && (
                    <Box component="span" sx={{ display: "block", color: "#64748b", fontSize: compact ? 9.5 : 11 }}>
                        {currentStart ? "Identificado às" : "Última posição às"}: {formatDate(currentStart?.identifiedAtUtc || fixTime, vehicle.utcZone)}
                    </Box>
                )}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: compact ? 10.5 : 13 }}>
                <strong>Anterior:</strong> {previous ? driverLabel(previous)
                    : loading ? "Carregando histórico..." : error ? "Histórico indisponível"
                        : "Nenhuma troca anterior registrada"}
                {previous && <AuthorizationFlag authorized={previous.driverAuthorized} compact={compact} />}
                {previous && (
                    <Box component="span" sx={{ display: "block", color: "#64748b", fontSize: compact ? 9.5 : 11 }}>
                        Identificado às {formatDate(previous.identifiedAtUtc, vehicle.utcZone)}
                        {currentStart && ` · Troca identificada às ${formatDate(currentStart.identifiedAtUtc, vehicle.utcZone)}`}
                    </Box>
                )}
            </Typography>
            {!compact && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.75 }}>Últimas identificações registradas</Typography>
                    {passages.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                            {loading ? "Carregando histórico..." : error
                                ? "Não foi possível consultar o histórico." : "Nenhuma troca registrada para este veículo."}
                        </Typography>
                    ) : passages.map(change => (
                        <Typography key={`${Date.parse(change.identifiedAtUtc)}:${change.driverIdentifier}`} sx={{ fontSize: 12, py: 0.5 }}>
                            {formatDate(change.identifiedAtUtc, vehicle.utcZone)} · {driverLabel(change)}<AuthorizationFlag authorized={change.driverAuthorized} compact={compact} />
                        </Typography>
                    ))}
                    <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 1 }}>
                        A exportação inclui somente os dados exibidos: até cinco identificações e, quando necessário, a última posição do condutor atual.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default DriverChangeSummary;
